import { BrowserRouter, useLocation } from "react-router-dom";
import { QueryClient, QueryCache, MutationCache, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";

// Contexts
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { PendingIntentProvider } from "@/contexts/PendingIntentContext";
import { PreferencesProvider, usePreferences } from "@/contexts/PreferencesContext";

// App components
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AnalyticsInitializer } from "@/components/AnalyticsInitializer";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { shouldReportReactQueryError } from "@/lib/sentry/reactQueryReporting";

// Hooks
import { useIOSStatusBar } from "@/hooks/useIOSStatusBar";
import { useLocationSync } from "@/hooks/useLocationSync";
import { useTagsPrefetch } from "@/hooks/useTagsPrefetch";
import { useDeepLinks } from "@/hooks/app/useDeepLinks";
import { useInviteHandoff } from "@/hooks/app/useInviteHandoff";
import { useNativeAppLifecycle } from "@/hooks/app/useNativeAppLifecycle";
import { useNativeKeyboardInsets } from "@/hooks/app/useNativeKeyboardInsets";
import { usePushBootstrap } from "@/hooks/app/usePushBootstrap";
import { useResumeQueryCacheLifecycle } from "@/hooks/app/useResumeQueryCacheLifecycle";
import { useRouteAnalytics } from "@/hooks/app/useRouteAnalytics";
import { useRouteScrollReset } from "@/hooks/app/useRouteScrollReset";

// Routes
import { AppRoutes } from "@/routes/AppRoutes";

// Configure QueryClient with global error handlers for Sentry
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      // Units nobody is looking at leave memory after half an hour; the
      // bounded disk cache (cachePersistence) is the layer across sessions.
      // A day here kept a heavy user's whole history resident.
      gcTime: 30 * 60 * 1000,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (!shouldReportReactQueryError(error, query.meta)) return;

      // Send React Query errors to Sentry
      Sentry.captureException(error, {
        contexts: {
          reactQuery: {
            queryKey: query.queryKey,
            queryHash: query.queryHash,
          },
        },
        tags: {
          errorSource: "react-query",
          errorType: "query",
        },
        extra: {
          queryKey: query.queryKey,
          queryHash: query.queryHash,
        },
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, variables, context, mutation) => {
      if (!shouldReportReactQueryError(error, mutation.meta)) return;

      // Send React Query mutation errors to Sentry
      Sentry.captureException(error, {
        contexts: {
          reactQuery: {
            mutationId: mutation.mutationId,
            state: mutation.state?.status,
          },
        },
        tags: {
          errorSource: "react-query",
          errorType: "mutation",
        },
        extra: {
          mutationId: mutation.mutationId,
          variables,
        },
      });
    },
  }),
});

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <PreferencesProvider>
          <PendingIntentProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <ErrorBoundary>
                  <AppContent />
                </ErrorBoundary>
              </BrowserRouter>
            </TooltipProvider>
          </PendingIntentProvider>
        </PreferencesProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

const AppContent = () => {
  const { pathname, search, hash } = useLocation();
  const { isAuthenticated, isLoading, user } = useAuth();
  const { preferences } = usePreferences();
  const queryClient = useQueryClient();
  const userId = user?.user_id ?? null;

  useIOSStatusBar();
  useRouteAnalytics({ pathname, search, isAuthenticated, isLoading });
  useRouteScrollReset({ pathname, search, hash });
  useLocationSync({ enabled: isAuthenticated });
  useTagsPrefetch();
  const refreshResumeQueries = useResumeQueryCacheLifecycle({ isAuthenticated, queryClient, userId });
  useInviteHandoff({ search, isLoading, isAuthenticated });
  useNativeKeyboardInsets();
  useNativeAppLifecycle({ isAuthenticated, queryClient, refreshResumeQueries, userId });
  usePushBootstrap({
    isAuthenticated,
    isLoading,
    notificationsEnabled: preferences.notificationsEnabled,
    queryClient,
  });
  useDeepLinks({ isAuthenticated, isLoading });

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-1">
        <AppRoutes />
      </main>
      <AnalyticsInitializer />
    </div>
  );
};

export default App;
